import React from 'react'
import { IdentityConsumer } from '#src/containers/Identity'
import { ApolloConsumer } from 'react-apollo'
import GetMessagingUsersDocument from './getMessagingUsers.gql'
import {
  GetMessagingUsersQuery as GetMessagingUsersQueryType,
  GetMessagingUsersQueryVariables,
  MessagingUserType
} from '#src/generated/graphql'
import {
  ChatProvider,
  MessageNewVideoChat,
  MessageComponentProps,
  FirebaseMessage
} from '@deal/chat-firebase'
import {
  ProductCuration,
  ServiceCuration,
  SimpleCuratedOrder,
  FeedbackRequest,
  DealbotRequestConfirmation,
  HowDealWorks,
  ExpertJoined,
  CuratedOrderList,
  ConsumerNotifyExpertCuratedOrder,
  NewCustomExpertCuratedOrder,
  Referral,
  ProductQuiz,
  ContactCollection
} from '#src/components/Messages'
import { withMediaUpload } from './withMediaUpload'
import { withVideoChat } from './withVideoChat'
export { withMediaUpload, withVideoChat }
import config from '#src/config'
import curatedAvatarUrl from './curated-avatar.png'
import '@deal/chat-firebase/dist/index.esm.css'

const MyMessageNewVideoChat = withVideoChat(MessageNewVideoChat) as React.ComponentClass<
  MessageComponentProps
> & {
  handlesMessage: (message: FirebaseMessage) => boolean
}

const ConsumerChatProvider: React.SFC = ({ children }) => {
  return (
    <IdentityConsumer>
      {({ myself }) => {
        if (!myself) {
          return children
        }

        return (
          /**
           * TODO: Update `ChatProvider` to have more React-idiomatic props (i.e. non-imperative APIs):
           *
           *   - `onLoadUserProfiles`, `userProfiles` instead of `fetchUserProfiles`
           *   - `onAuthenticate`, `authenticationToken` instead of `authenticate`
           */
          <ApolloConsumer>
            {client => (
              <ChatProvider
                firebaseApiKey={config.get('firebase.api_key')}
                firebaseProjectId={config.get('firebase.project_id')}
                fetchUserProfiles={userIds =>
                  client
                    .query<GetMessagingUsersQueryType, GetMessagingUsersQueryVariables>({
                      query: GetMessagingUsersDocument,
                      variables: { ids: userIds }
                    })
                    .then(({ data }) =>
                      data.messagingUsers.map(messagingUser => {
                        return {
                          userId: messagingUser.id,
                          vanityId:
                            messagingUser.profile.__typename === 'BusinessUser'
                              ? messagingUser.profile.vanityId || undefined
                              : undefined,
                          displayName: messagingUser.displayName,
                          internalMessagingUser: messagingUser.type === MessagingUserType.USER,
                          type: messagingUser.type,
                          avatarUrl:
                            (messagingUser.avatar && messagingUser.avatar.url) ||
                            (messagingUser.type === MessagingUserType.USER
                              ? curatedAvatarUrl
                              : undefined),
                          expertRating:
                            (messagingUser.profile.__typename === 'BusinessUser' &&
                              messagingUser.profile.expertStats &&
                              messagingUser.profile.expertStats.averageRating) ||
                            undefined
                        }
                      })
                    )
                }
                userId={myself!.user.messagingUser.id}
                impersonating={myself.realUser.id !== myself.user.id}
                customMessageComponents={[
                  ProductCuration,
                  ServiceCuration,
                  SimpleCuratedOrder,
                  MyMessageNewVideoChat,
                  FeedbackRequest,
                  DealbotRequestConfirmation,
                  ExpertJoined,
                  HowDealWorks,
                  CuratedOrderList,
                  ConsumerNotifyExpertCuratedOrder,
                  NewCustomExpertCuratedOrder,
                  ProductQuiz,
                  Referral,
                  ContactCollection
                ]}
              >
                {children}
              </ChatProvider>
            )}
          </ApolloConsumer>
        )
      }}
    </IdentityConsumer>
  )
}

export default ConsumerChatProvider
